import api from './api';

export const dashboardService = {
  // =============================================================================
  // DASHBOARD SUMMARY
  // =============================================================================

  async getTrialDashboardSummary() {
    try {
      const response = await api.get('/dashboard/trials/summary');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch dashboard summary');
    }
  },
  // Get dashboard summary
  async getDashboardSummary() {
    try {
      const response = await api.get('/dashboard/summary');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch dashboard summary');
    }
  },

  // Get trials by status
  async getTrialsByStatus() {
    try {
      const response = await api.get('/dashboard/trials-by-status');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trials by status');
    }
  },

  // Get observations by month
  async getObservationsByMonth() {
    try {
      const response = await api.get('/dashboard/observations-by-month');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch observations by month');
    }
  },

  // Get recent activities
  async getRecentActivities() {
    try {
      const response = await api.get('/dashboard/activities');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch recent activities');
    }
  },

  // Last N trials for dashboard "Recent Activities" (trial-created list)
  async getRecentTrials() {
    try {
      const response = await api.get('/dashboard/recent-trials');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch recent trials');
    }
  },

  // Get total user count for incharges
  async getTotalUserCount() {
    try {
      const response = await api.get('/dashboard/users/count');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch total user count');
    }
  },

  // Get active locations count
  async getActiveLocationsCount() {
    try {
      const response = await api.get('/location-management/count/active');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch active locations count');
    }
  },

  // =============================================================================
  // LOCATION STATISTICS
  // =============================================================================

  // Get location statistics
  async getLocationStats() {
    try {
      const response = await api.get('/location-hierarchy/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch location statistics');
    }
  },

  // Get location hierarchy
  async getLocationHierarchy() {
    try {
      const response = await api.get('/location-hierarchy/hierarchy');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch location hierarchy');
    }
  },

  // =============================================================================
  // TRIAL STATISTICS
  // =============================================================================

// Get trial statistics (dashboard-specific)
async getTrialStats() {
  try {
    const response = await api.get('/dashboard/trial-stats');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch trial statistics');
  }
},

  // Get trials by month
  async getTrialsByMonth(year) {
    try {
      const response = await api.get(`/trials/stats/by-month?year=${year}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trials by month');
    }
  },

  // Get trials by status count
  async getTrialsByStatusCount() {
    try {
      const response = await api.get('/trials/stats/by-status');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trials by status count');
    }
  },

  // =============================================================================
  // OFFICER STATISTICS
  // =============================================================================

  // Get officer statistics
  async getOfficerStats() {
    try {
      const response = await api.get('/officers/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch officer statistics');
    }
  },

  // Get officer performance metrics
  async getOfficerPerformance(officerId, dateRange = {}) {
    try {
      const response = await api.get(`/officers/${officerId}/performance`, {
        params: dateRange,
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch officer performance');
    }
  },

  // =============================================================================
  // CROP STATISTICS
  // =============================================================================

  // Get crop statistics
  async getCropStats() {
    try {
      const response = await api.get('/crops/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch crop statistics');
    }
  },

  // Get crop group statistics
  async getCropGroupStats() {
    try {
      const response = await api.get('/crop-groups/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch crop group statistics');
    }
  },

  // =============================================================================
  // SEASON STATISTICS
  // =============================================================================

  // Get season statistics
  async getSeasonStats() {
    try {
      const response = await api.get('/seasons/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch season statistics');
    }
  },

  // Get trials by season
  async getTrialsBySeason() {
    try {
      const response = await api.get('/seasons/trials');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch trials by season');
    }
  },

  // =============================================================================
  // FIELD OPERATIONS STATISTICS
  // =============================================================================

  // Get field operations statistics
  async getFieldOperationsStats() {
    try {
      const response = await api.get('/field-operations/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch field operations statistics');
    }
  },

  // Get field operations by type
  async getFieldOperationsByType() {
    try {
      const response = await api.get('/field-operations/stats/by-type');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch field operations by type');
    }
  },

  // =============================================================================
  // SOIL MANAGEMENT STATISTICS
  // =============================================================================

  // Get soil management statistics
  async getSoilManagementStats() {
    try {
      const response = await api.get('/soil-management/stats');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch soil management statistics');
    }
  },

  // Get soil types distribution
  async getSoilTypesDistribution() {
    try {
      const response = await api.get('/soil-management/soil-types/distribution');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch soil types distribution');
    }
  },

  // =============================================================================
  // COMPREHENSIVE DASHBOARD DATA
  // =============================================================================

  // Get comprehensive dashboard data
  async getComprehensiveDashboardData() {
    try {
      const [
        summary,
        trialsByStatus,
        observationsByMonth,
        recentActivities,
        locationStats,
        trialStats,
        officerStats,
        cropStats,
        seasonStats,
        fieldOpsStats,
        soilStats
      ] = await Promise.all([
        this.getDashboardSummary(),
        this.getTrialsByStatus(),
        this.getObservationsByMonth(),
        this.getRecentActivities(),
        this.getLocationStats(),
        this.getTrialStats(),
        this.getOfficerStats(),
        this.getCropStats(),
        this.getSeasonStats(),
        this.getFieldOperationsStats(),
        this.getSoilManagementStats()
      ]);

      return {
        summary,
        trialsByStatus,
        observationsByMonth,
        recentActivities,
        locationStats,
        trialStats,
        officerStats,
        cropStats,
        seasonStats,
        fieldOpsStats,
        soilStats
      };
    } catch (error) {
      throw new Error('Failed to fetch comprehensive dashboard data');
    }
  },

  // Get dashboard data by role
  async getDashboardDataByRole(userRole) {
    try {
      let dashboardData = {};

      switch (userRole) {
        case 'admin':
          dashboardData = await this.getComprehensiveDashboardData();
          break;
        case 'supervisor':
          dashboardData = {
            summary: await this.getDashboardSummary(),
            trialsByStatus: await this.getTrialsByStatus(),
            observationsByMonth: await this.getObservationsByMonth(),
            recentActivities: await this.getRecentActivities(),
            trialStats: await this.getTrialStats(),
            cropStats: await this.getCropStats()
          };
          break;
        case 'officer':
          dashboardData = {
            summary: await this.getDashboardSummary(),
            recentActivities: await this.getRecentActivities(),
            trialStats: await this.getTrialStats()
          };
          break;
        default:
          dashboardData = {
            summary: await this.getDashboardSummary(),
            recentActivities: await this.getRecentActivities()
          };
      }

      return dashboardData;
    } catch (error) {
      throw new Error('Failed to fetch role-based dashboard data');
    }
  },
};
